import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../shell/PublicLayout";
import Badge from "../components/Badge";
import Button from "../components/Button";
import { showTeamMember } from "../../service/service";
import { paths } from "../routing/paths";
import portrait from "../../assets/aboutpic1.png";

/*
 * /about-us in the redesign — replaces the legacy AboutUs (Banner +
 * Section1/2/3). Same copy, same user/our-team endpoint for the team grid;
 * navy hero + white prose cards in the shared page grammar. The team
 * section degrades gracefully: while the API loads it shows skeleton
 * cards, and if it fails or returns nothing the thank-you paragraph
 * stands alone — never a broken grid.
 */

const EXTERNAL_SITES = [
  { label: "maamarimonline.org", href: "https://maamarimonline.org" },
  { label: "stumptherabbi.org", href: "http://stumptherabbi.org/" },
  { label: "theyeshiva.net", href: "https://www.theyeshiva.net/" },
];

function SectionHeading({ children }) {
  return (
    <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(20px, 4.5vw, 24px)", lineHeight: 1.3 }}>
      {children}
    </h2>
  );
}

export default function AboutPage() {
  // null = loading, [] = failed/empty (section collapses to the paragraph)
  const [team, setTeam] = useState(null);

  useEffect(() => {
    let cancelled = false;
    showTeamMember()
      .then((result) => !cancelled && setTeam(result?.status && Array.isArray(result.data) ? result.data : []))
      .catch(() => !cancelled && setTeam([]));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PublicLayout>
      <Helmet>
        <title>About | Inside Chassidus</title>
      </Helmet>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">About</span>
        </nav>

        {/* Navy hero */}
        <section
          className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex gap-5 items-start flex-wrap"
          style={{ padding: "clamp(20px, 5vw, 32px)" }}
        >
          <div className="flex-1 min-w-[240px] flex flex-col gap-2">
            <span className="self-start">
              <Badge variant="onNavy">ABOUT</Badge>
            </span>
            <h1 className="m-0 font-bold text-white tracking-[-0.4px]" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
              Rabbi Paltiel and Inside Chassidus
            </h1>
            <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">
              Resources for studying Chassidic philosophy from the inside — inside the text and the
              original sources — in an archive of 15,000+ recorded classes.
            </p>
          </div>
        </section>

        {/* About Rabbi Paltiel */}
        <section className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised flex flex-col gap-5" style={{ padding: "clamp(20px, 5vw, 32px)" }}>
          <SectionHeading>About Rabbi Paltiel</SectionHeading>
          <div className="flex gap-6 flex-wrap items-start">
            <img
              src={portrait}
              alt="Rabbi Yossi Paltiel"
              className="rounded-xl w-full max-w-[280px] flex-shrink-0 object-cover"
            />
            <div className="flex-1 min-w-[260px] flex flex-col gap-3 text-[16px] leading-7 text-ic-body">
              <p className="m-0">
                Rabbi Yossi Paltiel, Founder of InsideChassidus.org, is a popular teacher and mentor with a
                gift for communicating his passion for Torah and Judaism.
              </p>
              <p className="m-0">
                His classes weave together classic commentaries, Jewish Law, history and philosophy, personal
                stories, and a deep knowledge of Chassidus and Kabbala into a whole that's both intellectually
                challenging and heartwarming.
              </p>
              <p className="m-0">
                He currently teaches at Yeshivas Tomchei Temimim Chovevei Torah (United Lubavitcher Yeshiva),
                Machon L'Yahadus Women's Institute for the Study of Judaism, and at the Beis Midrash L'Nashim
                (Women's and Girls' Beis Midrash and Seminary for Higher Learning under the Directorship of
                Rebbetzin Ariella Benhiyoun) at 770 Eastern Parkway, Crown Heights, Brooklyn.
              </p>
              <p className="m-0">
                He also gives lectures at many events in Brooklyn and travels to speak in Jewish communities
                throughout the country.
              </p>
              <div className="pt-2">
                <Link to={paths.contact()}>
                  <Button size="lg">Contact Rabbi Paltiel</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* About Inside Chassidus */}
        <section className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised flex flex-col gap-4" style={{ padding: "clamp(20px, 5vw, 32px)" }}>
          <SectionHeading>About Inside Chassidus</SectionHeading>
          <p className="m-0 text-[16px] leading-7 text-ic-body max-w-[720px]">
            Our goal is to provide resources for studying Chassidic philosophy from the inside — inside the
            text and the original sources. The audio archive includes individual lectures, short series, and
            extensive detailed series taught at an advanced level. We hope that students of Chassidus at all
            levels will find the archive inspiring and educational.
          </p>
          <div className="flex flex-col gap-1">
            <span className="text-[16px] font-bold leading-6 text-black">Links to other websites</span>
            <ul className="m-0 p-0 list-none flex flex-col">
              {EXTERNAL_SITES.map((site) => (
                <li key={site.href}>
                  <a
                    href={site.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ic-blue text-[16px] font-semibold underline inline-flex items-center min-h-[44px] hover:text-ic-blueHover"
                  >
                    {site.label} ↗
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* The team — API-backed, graceful when the endpoint has nothing */}
        <section className="flex flex-col gap-4">
          <SectionHeading>The Inside Chassidus team</SectionHeading>
          <p className="m-0 text-[16px] leading-7 text-ic-body max-w-[720px]">
            Over the past years, many people have given their time and energy to the project of bringing
            Rabbi Paltiel's recorded classes to the public through the internet. Thank you all and may
            Hashem bless you and your families.
          </p>

          {team === null && (
            <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 240px), 1fr))" }} aria-busy="true" aria-label="Loading team members">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="h-72 rounded-xl bg-ic-lightGray animate-pulse" />
              ))}
            </div>
          )}

          {team !== null && team.length > 0 && (
            <ul className="m-0 p-0 list-none grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 240px), 1fr))" }}>
              {team.map((member) => (
                <li key={member.id} className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden flex flex-col">
                  {member.url && (
                    <img src={member.url} alt="" className="w-full h-48 object-cover object-top" />
                  )}
                  <div className="p-5 flex flex-col gap-1.5">
                    <span className="text-[18px] font-bold leading-7 text-black ic-wrap-anywhere">{member.title}</span>
                    {member.designation && (
                      <span className="text-[14px] font-semibold leading-5 text-ic-slate">{member.designation}</span>
                    )}
                    {member.description && (
                      <p className="m-0 text-[14px] leading-6 text-ic-body">{member.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </PublicLayout>
  );
}
